import './add-product.scss';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useRef, useState, Dispatch, SetStateAction } from 'react';
import { Button } from '../../../components/form/button';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import classNames from 'classnames';
import { DateTimePicker } from '../../../components/date-picker/date-time-picker';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Product, ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { displayMoneyFormat, safeRound } from '../../../util/util';
import { Location } from '../../../clients/quipli-service/location/location';
import { DateTime, Duration, Interval } from 'luxon';
import { ceilToStep, findNextOpenTimeAfter } from '../../../util/date-time-util';
import {
  CalculateRentalPriceResult,
  CalculateSalePriceResult,
  CustomOrderPriceRequest,
  RentalProductPrice,
  SaleProductPrice,
} from '../../../clients/quipli-service/pricing/pricing';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { OrderSection } from '../order-section/order-section';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { OrderType } from '../../../clients/quipli-service/order/order';
import {
  OrderInputItem,
  OrderInputRental,
  OrderInputSale,
} from '../../../clients/quipli-service/order/order-calculation';
import { toast } from '../../../util/toasts';
import { EditableField, Field, FieldLabel } from '../../../components/field/field';
import { GroupProductPrice, PriceOption, SearchProductPrice } from './search-product-price';
import { InventoryGantt } from '../../inventory-dashboard/inventory-gantt/inventory-gantt';
import { Spinner } from '../../../components/spinner/spinner';
import { Divider } from '../../../components/divider/divider';
import { groupBy } from 'lodash';
import { usePricingApiV1 } from '../../../clients/quipli-service/pricing/pricing-api-v1';
import { CustomProductForm } from './components/custom-product-form';
import { startEndDateHours } from '../../../hooks/use-location-interval-helpers';

const DISTANT_FUTURE_DATETIME = DateTime.now().plus({ years: 1000 });

type AddProductType = 'Rental' | 'Sale' | 'Part' | 'Service' | 'Custom';

type AddonOptions = Pick<Product, 'id' | 'name'> & {
  rentalPrice?: CalculateRentalPriceResult;
  salePrice?: CalculateSalePriceResult;
};
type SelectedAddons = Record<number, { active: boolean; addonFor: OrderType }>;

export const AddProduct = ({
  locationId,
  userId,
  openOnStart = true,
  isCycleBilled,
  cycleLength = Duration.fromObject({ days: 28 }),
  orderId,
  isCashCustomer = false,
  setOrderInputs,
  newOrderInput,
  isTaxInclusive = false,
  taxCodeId,
}: {
  locationId?: number;
  userId?: number;
  isCycleBilled: boolean;
  cycleLength?: Duration;
  openOnStart?: boolean;
  orderId?: number;
  isCashCustomer?: boolean;
  setOrderInputs?: Dispatch<SetStateAction<OrderInputItem[]>>;
  newOrderInput?: (orderInputItem: OrderInputItem) => void;
  isTaxInclusive?: boolean;
  taxCodeId?: string;
}) => {
  const { getLocationById, getMultipleConfigurations } = useAPI();
  // States
  const [type, setType] = useState<AddProductType>('Rental');
  const [location, setLocation] = useState<Location>();
  const [cycleBillingEnabled, setCycleBillingEnabled] = useState(false);
  const [customOrderRentalRatesEnabled, setCustomOrderRentalRatesEnabled] = useState(false);
  const [saleSerialTrackingEnabled, setSaleSerialTrackingEnabled] = useState<boolean>(false);
  const [customProductEnabled, setCustomProductEnabled] = useState<boolean>(false);
  const [onlyShowStoreHoursOnDateTimePicker, setOnlyShowStoreHoursOnDateTimePicker] = useState<boolean>(false);

  // Vars
  const collapsibleRef = useRef<CollapsibleRef>(null);

  // Callbacks
  const collapse = () => {
    collapsibleRef.current?.toggle(false);
  };

  const onSubmit = ({ input, multipleInputs }: { input: OrderInputItem; multipleInputs: OrderInputItem[] }) => {
    setOrderInputs && setOrderInputs((prev) => [...prev, ...multipleInputs]);
    if (multipleInputs.length > 1) {
      for (const input of multipleInputs) {
        newOrderInput && newOrderInput(input);
      }
    } else {
      newOrderInput && newOrderInput(input);
    }
  };

  useEffect(() => {
    if (locationId === undefined) return;

    const getLocation = async () => {
      const l = await getLocationById(locationId);
      setLocation(l);
    };

    getMultipleConfigurations([
      'CONF_CYCLE_BILLING',
      'CONF_CUSTOM_ORDER_RENTAL_RATES',
      'CONF_SALE_SERIAL_TRACKING',
      'CONF_ENABLE_CUSTOM_PRODUCTS',
      'CONF_USE_STORE_HOURS_DATE_PICK',
    ]).then((configs) => {
      setCycleBillingEnabled(configs.find((c) => c.variable === 'CONF_CYCLE_BILLING')?.value === '1');
      setCustomOrderRentalRatesEnabled(
        configs.find((c) => c.variable === 'CONF_CUSTOM_ORDER_RENTAL_RATES')?.value === '1',
      );
      setSaleSerialTrackingEnabled(configs.find((c) => c.variable === 'CONF_SALE_SERIAL_TRACKING')?.value === '1');
      setCustomProductEnabled(configs.find((c) => c.variable === 'CONF_ENABLE_CUSTOM_PRODUCTS')?.value === '1');
      setOnlyShowStoreHoursOnDateTimePicker(
        configs.find((c) => c.variable === 'CONF_USE_STORE_HOURS_DATE_PICK')?.value === '1',
      );
    });
    getLocation();
  }, [locationId]);

  useEffect(() => {
    openOnStart && collapsibleRef.current?.toggle(true);
  }, []);

  useEffect(() => {
    isCashCustomer && setType('Sale');
  }, [isCashCustomer]);

  return (
    <div className="order-add-product">
      <OrderSection>
        <Collapsible
          ref={collapsibleRef}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Add products to order
              </Typography>
              <Icon icon={isOpen ? 'Close' : 'Plus'} stroke="var(--quipli-black-800)" />
            </div>
          )}
        >
          <div className="flex flex-col gap-3">
            <div className="list-tabs">
              <div className="tabs-wrapper">
                <div
                  className={classNames('tab-item', { active: type === 'Rental' })}
                  onClick={() => setType('Rental')}
                  data-cy="rental_tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Rental' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    For rent
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === 'Sale' })}
                  onClick={() => setType('Sale')}
                  data-cy="sale-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Sale' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    For sale
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === 'Part' })}
                  onClick={() => setType('Part')}
                  data-cy="part-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Part' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Parts
                  </Typography>
                  <div className="divider-active"></div>
                </div>
                <div
                  className={classNames('tab-item', { active: type === 'Service' })}
                  onClick={() => setType('Service')}
                  data-cy="service-tab"
                >
                  <Typography
                    type="text-sm"
                    weight="medium"
                    className={type === 'Service' ? 'text-quipli-black-800' : 'text-grey-500'}
                  >
                    Services
                  </Typography>
                  <div className="divider-active"></div>
                </div>

                {customProductEnabled && (
                  <div
                    className={classNames('tab-item', { active: type === 'Custom' })}
                    onClick={() => setType('Custom')}
                    data-cy="custom-tab"
                  >
                    <Typography
                      type="text-sm"
                      weight="medium"
                      className={type === 'Custom' ? 'text-quipli-black-800' : 'text-grey-500'}
                    >
                      Custom
                    </Typography>
                    <div className="divider-active"></div>
                  </div>
                )}
              </div>
            </div>

            {type !== 'Custom'
              ? location && (
                  <ProductForm
                    orderId={orderId}
                    userId={userId}
                    isCycleBilled={isCycleBilled}
                    cycleLength={cycleLength}
                    location={location}
                    type={OrderType[type === 'Rental' ? 'Rental' : 'Sale']}
                    productType={type === 'Rental' || type === 'Sale' ? ProductType.Product : (type as ProductType)}
                    cycleBillingEnabled={cycleBillingEnabled}
                    customOrderRentalRatesEnabled={customOrderRentalRatesEnabled}
                    saleSerialTrackingEnabled={saleSerialTrackingEnabled}
                    onlyShowStoreHoursOnDateTimePicker={onlyShowStoreHoursOnDateTimePicker}
                    onSubmit={onSubmit}
                    onCollapse={collapse}
                    isTaxInclusive={isTaxInclusive}
                    taxCodeId={taxCodeId}
                  />
                )
              : location && (
                  <CustomProductForm
                    onSubmit={({ input }) => {
                      onSubmit({ input, multipleInputs: [input] });
                    }}
                    isCycleBilled={isCycleBilled}
                    cycleLength={cycleLength}
                    onCollapse={collapse}
                    location={location}
                    customOrderRentalRatesEnabled={customOrderRentalRatesEnabled}
                  />
                )}
          </div>
        </Collapsible>
      </OrderSection>
    </div>
  );
};

const ProductForm = ({
  orderId,
  userId,
  isCycleBilled,
  cycleLength = Duration.fromObject({ days: 28 }),
  location,
  type,
  productType,
  cycleBillingEnabled,
  customOrderRentalRatesEnabled,
  saleSerialTrackingEnabled,
  onlyShowStoreHoursOnDateTimePicker,
  onSubmit,
  onCollapse,
  isTaxInclusive = false,
  taxCodeId,
}: {
  orderId?: number;
  userId?: number;
  isCycleBilled: boolean;
  cycleLength?: Duration;
  location: Location;
  type: OrderType.Rental | OrderType.Sale;
  productType: ProductType;
  cycleBillingEnabled: boolean;
  customOrderRentalRatesEnabled: boolean;
  saleSerialTrackingEnabled: boolean;
  onlyShowStoreHoursOnDateTimePicker: boolean;
  onSubmit?: ({ input, multipleInputs }: { input: OrderInputItem; multipleInputs: OrderInputItem[] }) => void;
  onCollapse?: () => void;
  isTaxInclusive?: boolean;
  taxCodeId?: string;
}) => {
  const { listPrices } = usePricingApiV1();
  const {
    listProductAddons,
    calculatePriceAsAdmin,
    getAvailableCountByLocations,
    getRentalAvailability,
    confirmAvailability,
    getProduct,
  } = useAPI();

  const [productPrice, setProductPrice] = useState<PriceOption | null>(null);
  const [groupPrice, setGroupPrice] = useState<GroupProductPrice | null>(null);
  const [quantity, setQuantity] = useState<number | undefined>(1);
  const [startDateTime, setStartDateTime] = useState<DateTime | undefined>(
    ceilToStep(DateTime.local(), Duration.fromObject({ minutes: 30 })),
  );
  const [endDateTime, setEndDateTime] = useState<DateTime>();
  // customRentalRatesForCalc should be any modified rental rates that need to be passed in body to
  //      calculation api (either new custom rate OR going back to regular rate)
  const [customRentalRatesForCalc, setCustomRentalRatesForCalc] = useState<{
    [durationStr: string]: CustomOrderPriceRequest | undefined;
  }>({});
  const [calculatedPriceResult, setCalculatedPriceResult] = useState<CalculateRentalPriceResult>();
  const [calculatedSalePrice, setCalculatedSalePrice] = useState<number>();
  const [addons, setAddons] = useState<AddonOptions[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddons>({});
  const [addToOrderClicked, setAddToOrderClicked] = useState(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState<boolean>(false);
  const [unitsAvailable, setUnitsAvailable] = useState<{ locationId: number; count: number }[]>();
  const [availability, setAvailability] = useState<{ intervals: Interval; color: string }[]>([]);
  const [availableEndDates, setavailableEndDates] = useState<{ intervals: Interval; color: string }[]>([]);
  const [showProductCalendar, setShowProductCalendar] = useState<boolean>(false);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const [checkAvailability, setCheckAvailability] = useState<boolean>(true);

  const isSubmitDisabled = () => {
    if (!productPrice || !quantity || quantity < 0) return true;

    if (type === OrderType.Rental) {
      if (!startDateTime?.isValid) {
        return true;
      }
      if (!endDateTime) {
        return !cycleBillingEnabled;
      } else {
        if (!endDateTime.isValid) {
          return true;
        } else {
          return endDateTime <= startDateTime;
        }
      }
    }

    return false;
  };

  const clear = () => {
    setCalculatedPriceResult(undefined);
    setCalculatedSalePrice(undefined);
    setQuantity(1);
    setAddons([]);
    setSelectedAddons({});
    setOpenCalendar(false);
    setUnitsAvailable(undefined);
  };

  const submit = async () => {
    if (isSubmitDisabled()) return;

    const commonProps = {
      id: uuidv4(),
      productId: productPrice!.object.productId,
      quantity: quantity,
    };

    const product: OrderInputItem =
      type === OrderType.Rental
        ? ({
            ...commonProps,
            type: OrderType.Rental,
            start: startDateTime!,
            end: endDateTime,
            assignedUnits: [],
            customOrderRentalRates: Object.values(customRentalRatesForCalc).filter(
              (r) => !!r,
            ) as CustomOrderPriceRequest[],
          } as OrderInputRental)
        : ({
            ...commonProps,
            type: OrderType.Sale,
            isCustom: false,
            assignedUnits: [],
          } as OrderInputSale);

    const newProds: OrderInputItem[] = [];
    try {
      await checkProductAvailability(product);
      newProds.push(product);
    } catch (error: any) {
      // if main product fails we don't want to add addons
      toast(error.message, 'error');
      return;
    }

    // Now we add addons
    const addonsToAdd = Object.entries(selectedAddons)
      .filter((addon) => addon[1].active)
      .map(
        ([id, addon]) =>
          ({
            id: uuidv4(),
            type: addon.addonFor,
            productId: parseInt(id),
            quantity: 1,
            ...(addon.addonFor === OrderType.Rental
              ? {
                  start: startDateTime!,
                  end: endDateTime!,
                  assignedUnits: [],
                }
              : {
                  isCustom: false,
                }),
          } as OrderInputRental | OrderInputSale),
      );

    if (addonsToAdd.length) {
      for (const addon of addonsToAdd) {
        try {
          await checkProductAvailability(addon);
          newProds.push(addon);
        } catch (error: any) {
          toast(error.message, 'error');
        }
      }
    }

    onSubmit && onSubmit({ input: product, multipleInputs: newProds });
    toast('Product added to order.');

    clear();
    setProductPrice(null);
    setAddToOrderClicked(true);
  };

  const checkProductAvailability = async (product: OrderInputRental | OrderInputSale) => {
    await confirmAvailability({ ...product, locationId: location!.id });
  };

  const handleOnChangePrice = async (op: PriceOption | null) => {
    if (!location) return;

    setProductPrice(op);
    setCustomRentalRatesForCalc({});

    // Option removed
    if (!op) {
      clear();
      return;
    }

    let currentStart: DateTime;
    let currentEnd: DateTime;

    // Calculate sale price
    if (op.object.type === OrderType.Sale) {
      setStartDateTime(undefined);
      setEndDateTime(undefined);

      calculatePriceAsAdmin({ productId: op.object.productId, type: OrderType.Sale, locationId: location?.id }).then(
        (res) => {
          setCalculatedSalePrice((res as CalculateSalePriceResult).totalPrice);
        },
      );

      handleOnChangeAddons(op);
    }

    // Set rental dates for rental products
    else {
      if (startDateTime) {
        currentStart = startDateTime;
      } else {
        currentStart = ceilToStep(
          findNextOpenTimeAfter(
            location.hoursOfOperation,
            location.disabledDates,
            'rentalStart',
            DateTime.local({ zone: location.timezone }).plus(location.minimumLeadTime).plus(location.defaultLeadTime),
            [],
          ),
          Duration.fromObject({ minutes: 30 }),
        );
      }

      const rentalPrice = op.object as RentalProductPrice;
      if (endDateTime && addToOrderClicked) {
        currentEnd = endDateTime;
        setEndDateTime(endDateTime);
      } else {
        currentEnd = currentStart.plus({ [rentalPrice.durationUnit]: rentalPrice.durationValue });
        setEndDateTime(currentEnd);
      }
      setStartDateTime(currentStart);
    }
  };

  const filterAddonsCallback = (
    productFor: AddProductType,
    allowRent: boolean,
    allowSale: boolean,
    rentUnits: RentalProductPrice[],
    saleUnits: SaleProductPrice[],
  ) => {
    if (
      productFor === OrderType.Rental &&
      allowRent &&
      rentUnits.some(
        ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location!.id),
      )
    ) {
      return OrderType.Rental;
    }

    if (
      (productFor === OrderType.Sale || productFor === OrderType.Rental) &&
      allowSale &&
      saleUnits.some(
        ({ locationIds }) => !locationIds || locationIds.length === 0 || locationIds.includes(location!.id),
      )
    ) {
      return OrderType.Sale;
    }

    return undefined;
  };

  const handleOnChangeAddons = (op: PriceOption) => {
    if (!location) return;

    // Get addons
    listProductAddons(op.object.productId, location.id).then(async (prods) => {
      const _selected: SelectedAddons = {};
      const prices = await listPrices({
        productIds: prods.map((p) => p.id),
        locationId: location.id,
        type: 'All',
      }).then((rps) => groupBy(rps, 'productId'));
      const _addons = await Promise.all(
        prods
          .filter(
            (p) =>
              filterAddonsCallback(
                type,
                p.allowRent,
                p.allowSale,
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Rental) as RentalProductPrice[],
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Sale) as SaleProductPrice[],
              ) !== undefined,
          )
          .map(async (p) => {
            _selected[p.id] = {
              active: selectedAddons[p.id]?.active ?? false,
              addonFor: filterAddonsCallback(
                type,
                p.allowRent,
                p.allowSale,
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Rental) as RentalProductPrice[],
                (prices[p.id] ?? []).filter((price) => price.type === OrderType.Sale) as SaleProductPrice[],
              )!,
            };

            if (_selected[p.id].addonFor === OrderType.Rental) {
              const maxEndDate = startDateTime!.plus({ days: 28 });
              const rentalPrice = (await calculatePriceAsAdmin({
                productId: p.id,
                type: OrderType.Rental,
                start: startDateTime!,
                end: endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate,
                locationId: location.id,
              })) as CalculateRentalPriceResult;

              return { id: p.id, name: p.name, rentalPrice };
            } else {
              const salePrice = (await calculatePriceAsAdmin({
                productId: p.id,
                type: OrderType.Sale,
                locationId: location.id,
              })) as CalculateSalePriceResult;

              return { id: p.id, name: p.name, salePrice };
            }
          }),
      );

      setSelectedAddons(_selected);
      setAddons(_addons);
    });
  };

  useEffect(() => {
    clear();
    setProductPrice(null);
  }, [type, productType]);

  useEffect(() => {
    if (!endDateTime && !cycleBillingEnabled) {
      return;
    }

    // If the end date is before the start date, clear the calculated price
    if (startDateTime && endDateTime && startDateTime >= endDateTime) {
      setCalculatedPriceResult(undefined);
      return;
    }

    if (!productPrice || !startDateTime) {
      return;
    }

    const maxEndDate = startDateTime.plus(cycleLength);

    calculatePriceAsAdmin({
      productId: productPrice.object.productId,
      type: OrderType.Rental,
      start: startDateTime,
      end: endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate,
      locationId: location?.id,
      orderId,
      userId,
      customOrderRentalRates: Object.values(customRentalRatesForCalc).filter((r) => !!r) as CustomOrderPriceRequest[],
      taxInclusive: isTaxInclusive,
      taxCodeId,
    }).then((res) => {
      const result = res as CalculateRentalPriceResult;
      setCalculatedPriceResult(result);
    });

    handleOnChangeAddons(productPrice);
  }, [
    productPrice?.object.productId,
    startDateTime,
    endDateTime,
    isCycleBilled,
    customRentalRatesForCalc,
    isTaxInclusive,
  ]);

  useEffect(() => {
    const manageAvailability = async () => {
      let maxEndDate, finalEndDate;

      // Want to fetch the tracking type so we can determine if we need to do availability checks
      const product = await getProduct(productPrice!.object.productId);
      const trackingType = product.trackingType;

      const untracked = trackingType == ProductTrackingType.NonInventory;

      setCheckAvailability(!untracked);

      if (productPrice?.object.type === OrderType.Rental && !untracked) {
        if (!endDateTime && !cycleBillingEnabled) {
          unitsAvailable !== undefined && setUnitsAvailable(undefined);
          setShowProductCalendar(false);
          return;
        }

        if (!productPrice || !startDateTime || !location) {
          unitsAvailable !== undefined && setUnitsAvailable(undefined);
          setShowProductCalendar(false);
          return;
        }

        maxEndDate = startDateTime.plus(cycleLength);
        finalEndDate = endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate;
      }

      if (productPrice && !untracked) {
        setIsAvailabilityLoading(true);
        getAvailableCountByLocations(
          productPrice.object.productId,
          productPrice.object.type,
          startDateTime ?? DateTime.now(),
          finalEndDate ?? DateTime.now(),
        ).then((res) => {
          setIsAvailabilityLoading(false);
          setUnitsAvailable(res);
        });
        setShowProductCalendar(true);
      } else {
        setUnitsAvailable(undefined);
      }
    };

    if (productPrice?.object.productId) {
      manageAvailability();
    }
  }, [productPrice?.object.productId, location, startDateTime, endDateTime]);

  useEffect(() => {
    if (type !== OrderType.Rental || !productPrice || !location || quantity === undefined || quantity < 0) {
      return;
    }

    getRentalAvailability(productPrice.object.productId, location.id, quantity).then((res) =>
      setAvailability(
        res
          .filter((a) => a !== undefined)
          .map((a) => {
            return {
              intervals: Interval.fromDateTimes(a.start!, a.end || DISTANT_FUTURE_DATETIME),
              color: 'var(--success-200)',
            };
          }),
      ),
    );
  }, [productPrice?.object.productId, location, quantity, type]);

  useEffect(() => {
    if (startDateTime?.isValid) {
      if (availability.length && location) {
        const currentAvailabilityInterval = availability
          .map((m) => m.intervals)
          .reverse()
          .find((av) => {
            const startsBefore = av.start === undefined || av.start.diff(startDateTime).milliseconds < 0;
            const endsAfter = av.end === undefined || av.end.diff(startDateTime).milliseconds > 0;
            return startsBefore && endsAfter;
          });

        const availabilityEnd = currentAvailabilityInterval?.end?.plus(1);
        if (availabilityEnd !== undefined) {
          setavailableEndDates([
            {
              intervals: Interval.fromDateTimes(startDateTime, availabilityEnd),
              color: 'var(--success-200)',
            },
          ]);
        }
      }
    } else {
      setavailableEndDates([]);
    }
  }, [startDateTime, availability, location]);

  const { startDaysHours, endDaysHours } = startEndDateHours(
    startDateTime,
    endDateTime,
    location,
    onlyShowStoreHoursOnDateTimePicker,
  );

  return (
    <>
      <div className="flex flex-col gap-[4px]">
        <FieldLabel label="Product" isRequired dataCy="product_field" />
        <SearchProductPrice
          location={location}
          orderId={orderId}
          userId={userId}
          type={OrderType[type]}
          value={productPrice || undefined}
          onSelect={handleOnChangePrice}
          onGroupSelect={setGroupPrice}
          productType={productType}
        />
      </div>

      {productType !== ProductType.Service && (
        <Field
          label="Quantity"
          isRequired
          type="number"
          value={productPrice === null ? null : 1}
          disabled={productPrice === null}
          onChange={(qty?: number) => setQuantity(qty)}
          dataCy="product_quantity"
        />
      )}

      {type === OrderType.Sale && checkAvailability && productPrice?.object.productId && groupPrice && (
        <AvailabilityText
          availability={
            groupPrice.trackingType === ProductTrackingType.SerialUnits && saleSerialTrackingEnabled
              ? unitsAvailable?.find((l) => l.locationId === location?.id)?.count ?? 0
              : groupPrice.stock?.find((s) => s.locationId === location?.id)?.saleStock || groupPrice.saleStock || 0
          }
          selectedQty={quantity}
        />
      )}

      {type === OrderType.Rental && (
        <>
          <div className="flex flex-col md:flex-row gap-y-[16px] gap-x-[24px]">
            <div className="flex-grow">
              <DateTimePicker
                label="Rent from"
                value={startDateTime}
                onChange={setStartDateTime}
                timezone={location?.timezone}
                minTime={startDaysHours?.start}
                maxTime={startDaysHours?.end}
                highlightedIntervals={availability}
                usePortal
                data-cy="product_start_date"
              />
            </div>

            <div className="flex-grow">
              <DateTimePicker
                label="Rent to"
                value={endDateTime}
                onChange={setEndDateTime}
                timezone={location?.timezone}
                minTime={endDaysHours?.start}
                maxTime={endDaysHours?.end}
                highlightedIntervals={availableEndDates}
                usePortal
                clearable={isCycleBilled}
                data-cy="product_end_date"
              />
            </div>
          </div>

          {location && productPrice?.object.productId && groupPrice && (
            <>
              <div className="flex items-start justify-between gap-2">
                {isAvailabilityLoading ? (
                  <div className="flex justify-center w-full">
                    <Spinner />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-1">
                      {unitsAvailable && (
                        <>
                          <AvailabilityText
                            availability={unitsAvailable.find((u) => u.locationId === location.id)?.count || 0}
                            selectedQty={quantity}
                            complementMessage={` at ${location.name}`}
                          />

                          {checkAvailability &&
                            (groupPrice.trackingType === ProductTrackingType.SerialUnits ||
                              (groupPrice.stock && groupPrice.stock.length > 0)) &&
                            unitsAvailable.length > 1 && (
                              <AvailabilityText
                                availability={unitsAvailable
                                  .filter((u) => u.locationId !== location.id)
                                  .reduce((acc, u) => acc + u.count, 0)}
                                selectedQty={quantity}
                                complementMessage={' at other locations'}
                              />
                            )}
                        </>
                      )}
                    </div>

                    {showProductCalendar && startDateTime?.isValid && (
                      <Button className="button-secondary" onClick={() => setOpenCalendar((prev) => !prev)}>
                        {!openCalendar ? 'Open' : 'Close'} Calendar
                      </Button>
                    )}
                  </>
                )}
              </div>

              {showProductCalendar && openCalendar && !isAvailabilityLoading && startDateTime?.isValid && (
                <ProductInventoryCalendar
                  productId={productPrice?.object.productId}
                  startDateTime={startDateTime!}
                  endDateTime={endDateTime}
                  isCycleBilled={isCycleBilled}
                  cycleLength={cycleLength}
                />
              )}
            </>
          )}
        </>
      )}
      <div className="flex flex-col gap-[4px]">
        <FieldLabel label="Pricing Breakdown" />
        <>
          {productPrice?.object.type === OrderType.Rental && customOrderRentalRatesEnabled && (
            <div className={'flex-[0_1_calc(100%-20px)] show-prices'}>
              <table className={'invoice-table min-w-full'}>
                <thead>
                  <tr data-cy="rental_prices_table_headers">
                    {groupPrice?.rentalPrices.map((price: RentalProductPrice) => {
                      return (
                        <th key={`rental-price-head-${productPrice.object.id}-${price.id}`} className="text-left pl-2">
                          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                            {price.durationLabel ?? 'Price'}
                          </Typography>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr data-cy="rental_prices_table_prices_row">
                    {groupPrice?.rentalPrices.map((price: RentalProductPrice) => {
                      return (
                        <td key={`rental-price-body-${productPrice.object.id}-${price.id}`} className="p-2">
                          <EditableField
                            type="number"
                            value={customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] ?? price}
                            formattedValue={displayMoneyFormat(
                              safeRound(
                                (customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price ??
                                  price.price) / 100,
                              ),
                              location?.currency,
                            )}
                            canReset={
                              !!customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`] &&
                              customRentalRatesForCalc[`${price.durationValue}-${price.durationUnit}`]?.price !==
                                price.price
                            }
                            resetPosition="right"
                            onReset={() => {
                              setCustomRentalRatesForCalc({
                                ...customRentalRatesForCalc,
                                [`${price.durationValue}-${price.durationUnit}`]: undefined,
                              });
                            }}
                            onChange={(v) => {
                              setCustomRentalRatesForCalc({
                                ...customRentalRatesForCalc,
                                [`${price.durationValue}-${price.durationUnit}`]: {
                                  ...price,
                                  price: v * 100,
                                  action:
                                    v * 100 === price.originalPrice?.price
                                      ? 'remove'
                                      : price.originalPrice?.price
                                      ? 'update'
                                      : 'add',
                                  idToChange:
                                    v * 100 === price.originalPrice?.price
                                      ? price.id
                                      : price.originalPrice?.price
                                      ? price.id
                                      : undefined,
                                },
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
        <div className="flex">
          <div className="flex-[calc(50%_-_(24px_/_2))] flex-grow-0 rounded-[4px] bg-grey-50 px-[4px] py-[6px]">
            {!calculatedPriceResult && !calculatedSalePrice && <span className="invisible">b</span>}

            {!!calculatedPriceResult && (
              <>
                {calculatedPriceResult.componentPrices &&
                  calculatedPriceResult.componentPrices.map((componentPrice, index) => {
                    const productSelected = groupPrice;
                    const og = productSelected?.rentalPrices.find((rp) => {
                      return (
                        rp.durationValue == componentPrice.unitPrice.durationValue &&
                        rp.durationUnit == componentPrice.unitPrice.durationUnit &&
                        (!rp.orderId || rp.originalPrice?.id)
                      );
                    });
                    const originalPrice =
                      productSelected &&
                      (!!customRentalRatesForCalc[
                        `${componentPrice.unitPrice.durationValue}-${componentPrice.unitPrice.durationUnit}`
                      ]?.price ||
                        !!componentPrice.unitPrice.orderId)
                        ? og?.originalPrice?.price ?? og?.price
                        : undefined;
                    return (
                      <Typography key={`component-price-${index}`} type="text-sm" className="text-grey-500">
                        {safeRound(componentPrice.consumedUnits * 1000) / 1000} x{' '}
                        {
                          <s className={classNames('text-xs pr-1', { hidden: !componentPrice.unitPrice.orderId })}>
                            {displayMoneyFormat(
                              componentPrice.unitPrice.orderId && originalPrice ? originalPrice / 100 : 1,
                              location?.currency,
                            )}
                          </s>
                        }
                        {displayMoneyFormat(componentPrice.unitPrice.price / 100, location?.currency)}{' '}
                        {`Per ${componentPrice.unitPrice.durationValue} ${componentPrice.unitPrice.durationUnit}${
                          componentPrice.unitPrice.durationValue > 1 ? 's' : ''
                        }`}
                        {index === calculatedPriceResult.componentPrices.length - 1 && (
                          <>{` = ${displayMoneyFormat(calculatedPriceResult.totalPrice / 100, location?.currency)}`}</>
                        )}
                      </Typography>
                    );
                  })}
              </>
            )}

            {!!calculatedSalePrice && (
              <Typography type="text-sm">
                {`${displayMoneyFormat(calculatedSalePrice / 100, location?.currency)}`}
              </Typography>
            )}
          </div>
        </div>
      </div>
      {addons.length > 0 && (
        <div className="flex flex-col gap-[4px]">
          <Typography type="text-md" weight="medium" className="text-grey-700">
            Addons
          </Typography>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-cy="addons_wrapper">
            {addons.map((addon, index) => (
              <Checkbox
                key={`addon-${index}`}
                type="white"
                isChecked={selectedAddons[addon.id].active}
                label={
                  <span className="!text-grey-700 flex items-center gap-[6px] flex-wrap">
                    <span>{addon.name}</span>
                    <span className="font-medium">
                      {selectedAddons[addon.id].addonFor === OrderType.Sale && type === OrderType.Rental
                        ? 'Sale - '
                        : null}
                      {displayMoneyFormat(
                        ((selectedAddons[addon.id].addonFor === OrderType.Rental
                          ? addon.rentalPrice?.totalPrice
                          : addon.salePrice?.totalPrice) || 0) / 100,
                        location?.currency,
                      )}
                    </span>
                  </span>
                }
                onChange={(checked) =>
                  setSelectedAddons({
                    ...selectedAddons,
                    [addon.id]: { active: checked, addonFor: selectedAddons[addon.id].addonFor },
                  })
                }
              />
            ))}
          </div>
        </div>
      )}
      <div className="button-group">
        <Button className="button-secondary" onClick={onCollapse} data-cy="cancel_add_product_to_order">
          Cancel
        </Button>
        <Button
          className="button-primary"
          disabled={isSubmitDisabled()}
          onClick={submit}
          data-cy="add_product_to_order"
        >
          Add to order
        </Button>
      </div>
    </>
  );
};

const AvailabilityText = ({
  selectedQty,
  availability,
  complementMessage = '',
}: {
  selectedQty?: number;
  availability: number;
  complementMessage?: string;
}) => {
  return (
    <Typography
      type="text-sm"
      className={classNames({
        'text-quipli-black-500': availability,
        'text-warning-600': !availability || availability < (selectedQty || 1),
      })}
      dataCy="availability_text"
    >
      {availability > 0
        ? `There are ${availability} units available${complementMessage}.`
        : `No units are available during the selected time period${complementMessage}.`}
    </Typography>
  );
};

const ProductInventoryCalendar = ({
  productId,
  startDateTime,
  endDateTime,
  isCycleBilled,
  cycleLength,
}: {
  productId: number;
  startDateTime: DateTime;
  endDateTime?: DateTime;
  isCycleBilled: boolean;
  cycleLength: Duration;
}) => {
  const maxEndDate = startDateTime.plus(cycleLength);
  const end = endDateTime ? (isCycleBilled ? DateTime.min(endDateTime, maxEndDate) : endDateTime) : maxEndDate;
  const start = startDateTime.minus({ days: 2 });

  return (
    <div>
      <Divider />
      <div className="my-4">
        <InventoryGantt
          expandAll
          productIds={[productId]}
          startDateTime={start}
          duration={end.plus({ days: 2 }).diff(start, 'days')}
        />
      </div>
      <Divider />
    </div>
  );
};
